import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Edit3, Trash2, KeyRound, AlertTriangle, CheckCircle2, RefreshCw, Network, Wifi,
} from 'lucide-react';
import { olts, type OltSummary, type OltCreatePayload } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';

export default function Olts() {
  const [editing, setEditing] = useState<{ olt?: OltSummary } | null>(null);
  const { data: list, isLoading } = useQuery({
    queryKey: ['olts'],
    queryFn: () => olts.list(),
  });

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <PageHeader
        title="OLTs"
        subtitle={`${list?.length ?? '…'} chassis enregistré${(list?.length ?? 0) > 1 ? 's' : ''}`}
        actions={
          <button className="btn btn-primary btn-sm" onClick={() => setEditing({})}>
            <Plus size={11} />Ajouter un OLT
          </button>
        }
      />

      <div className="flex-1 overflow-auto p-4">
        {isLoading ? (
          <div className="text-text-faint text-center py-12 italic">Chargement…</div>
        ) : !list?.length ? (
          <div className="card card-pad text-center py-12">
            <Network size={32} className="mx-auto text-text-faint mb-3" />
            <div className="text-sm font-semibold">Aucun OLT enregistré</div>
            <div className="text-xs text-text-muted mt-1">Ajoute le premier pour commencer la discovery.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {list.map((o) => (
              <OltCard key={o.id} olt={o} onEdit={() => setEditing({ olt: o })} />
            ))}
          </div>
        )}
      </div>

      {editing && (
        <OltFormModal open={!!editing} onClose={() => setEditing(null)} olt={editing.olt} />
      )}
    </div>
  );
}

function OltCard({ olt, onEdit }: { olt: OltSummary; onEdit: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const test = useMutation({
    mutationFn: () => olts.testConnection(olt.id),
    onSuccess: (r) => {
      if (r.ok) {
        toast.push({ kind: 'success', title: 'Connection OK', detail: 'OLT atteint, creds valides.' });
        qc.invalidateQueries({ queryKey: ['olts'] });
      } else {
        toast.push({ kind: 'error', title: 'Connection failed', detail: r.error ?? 'unknown error' });
      }
    },
    onError: (e: any) => toast.push({ kind: 'error', title: 'Test failed', detail: e?.response?.data?.error ?? e.message }),
  });

  const statusColor =
    olt.status === 'online'  ? 'text-success'
  : olt.status === 'offline' ? 'text-danger'
                             : 'text-text-faint';

  return (
    <div className="card card-pad">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="w-9 h-9 rounded bg-surface-hi flex items-center justify-center text-brand shrink-0">
            <Network size={16} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm truncate">{olt.name}</span>
              <span className={`badge text-[10px] ${
                olt.status === 'online' ? 'badge-up' :
                olt.status === 'offline' ? 'badge-down' : 'badge'
              }`}>
                <span className={`dot ${
                  olt.status === 'online' ? 'dot-up' :
                  olt.status === 'offline' ? 'dot-down' : 'dot-warn'
                }`} />
                {olt.status}
              </span>
              {olt.has_cli_credentials
                ? <span className="badge badge-info text-[10px]"><KeyRound size={9} />CLI creds</span>
                : <span className="badge text-[10px] text-warning"><AlertTriangle size={9} />no creds</span>}
            </div>
            <div className="text-[11.5px] text-text-faint mt-0.5 font-mono">
              {olt.vendor} {olt.model} · <span className="text-brand">{olt.management_address}:{olt.cli_port}</span>
            </div>
            {olt.site_label && (
              <div className="text-[10.5px] text-text-muted mt-0.5">📍 {olt.site_label}</div>
            )}
          </div>
        </div>
        <div className="flex gap-1.5 shrink-0">
          <button className="btn btn-sm btn-ghost" onClick={() => test.mutate()} disabled={test.isPending} title="Test connection">
            {test.isPending
              ? <RefreshCw size={11} className="animate-spin" />
              : <CheckCircle2 size={11} />}
          </button>
          <button className="btn btn-sm" onClick={onEdit}>
            <Edit3 size={10} />Éditer
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-border">
        <Stat label="PONs" value={olt.counts.pons} />
        <Stat label="ONTs" value={olt.counts.onts} />
        <Stat label="FXS" value={olt.counts.fxs} />
      </div>

      <div className="flex items-center justify-between text-[10.5px] text-text-faint mt-2">
        <span>Driver <span className="font-mono">{olt.driver}</span></span>
        <span>{olt.last_discovered_at
          ? `last disc. ${new Date(olt.last_discovered_at).toLocaleString('fr', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`
          : 'never discovered'}</span>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="text-center">
      <div className="text-lg font-mono font-medium">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-text-faint">{label}</div>
    </div>
  );
}

function OltFormModal({ open, onClose, olt }: { open: boolean; onClose: () => void; olt?: OltSummary }) {
  const qc = useQueryClient();
  const toast = useToast();
  const isEdit = !!olt;
  const [form, setForm] = useState<OltCreatePayload>(() => ({
    name:               olt?.name ?? '',
    vendor:             olt?.vendor ?? 'adtran',
    model:              olt?.model ?? 'TA5000',
    driver:             olt?.driver ?? 'AdtranTa5000Driver',
    management_address: olt?.management_address ?? '',
    cli_port:           olt?.cli_port ?? 22,
    snmp_port:          olt?.snmp_port ?? 161,
    snmp_version:       olt?.snmp_version ?? 'v2c',
    notes:              olt?.notes ?? '',
    site_label:         olt?.site_label ?? '',
    cli_username:       '',
    cli_password:       '',
    snmp_community:     '',
  }));

  const upsert = useMutation({
    mutationFn: () => isEdit ? olts.update(olt!.id, form) : olts.create(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['olts'] });
      toast.push({ kind: 'success', title: isEdit ? 'OLT mis à jour' : 'OLT créé' });
      onClose();
    },
    onError: (e: any) => toast.push({ kind: 'error', title: 'Erreur', detail: e?.response?.data?.message ?? e.message }),
  });

  const destroy = useMutation({
    mutationFn: () => olts.destroy(olt!.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['olts'] });
      toast.push({ kind: 'success', title: 'OLT supprimé' });
      onClose();
    },
    onError: (e: any) => toast.push({ kind: 'error', title: 'Suppression refusée', detail: e?.response?.data?.detail ?? e.message }),
  });

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? `Éditer ${olt!.name}` : 'Nouvel OLT'} width="max-w-2xl">
      <form onSubmit={(e) => { e.preventDefault(); upsert.mutate(); }} className="space-y-3 text-sm">
        <div className="grid grid-cols-2 gap-3">
          <label className="block col-span-2">
            <span className="text-xs text-text-muted block mb-1">Nom<span className="text-danger ml-0.5">*</span></span>
            <input required className="input w-full" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Telkel TA5000 Montréal" />
          </label>

          <label className="block">
            <span className="text-xs text-text-muted block mb-1">Vendor<span className="text-danger ml-0.5">*</span></span>
            <select required className="input w-full" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })}>
              <option value="adtran">Adtran</option>
              <option value="huawei">Huawei</option>
              <option value="calix">Calix</option>
              <option value="nokia">Nokia</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-text-muted block mb-1">Model<span className="text-danger ml-0.5">*</span></span>
            <input required className="input w-full font-mono" value={form.model}
              onChange={(e) => setForm({ ...form, model: e.target.value })}
              placeholder="TA5000" />
          </label>

          <label className="block col-span-2">
            <span className="text-xs text-text-muted block mb-1">Driver class<span className="text-danger ml-0.5">*</span></span>
            <input required className="input w-full font-mono" value={form.driver}
              onChange={(e) => setForm({ ...form, driver: e.target.value })}
              placeholder="AdtranTa5000Driver" />
          </label>

          <label className="block">
            <span className="text-xs text-text-muted block mb-1">Management IP<span className="text-danger ml-0.5">*</span></span>
            <input required className="input w-full font-mono" value={form.management_address}
              onChange={(e) => setForm({ ...form, management_address: e.target.value })}
              placeholder="10.7.10.10" />
          </label>
          <label className="block">
            <span className="text-xs text-text-muted block mb-1">Site label</span>
            <input className="input w-full" value={form.site_label ?? ''}
              onChange={(e) => setForm({ ...form, site_label: e.target.value })}
              placeholder="Montréal · HQ" />
          </label>

          <label className="block">
            <span className="text-xs text-text-muted block mb-1">CLI port</span>
            <input type="number" className="input w-full font-mono" value={form.cli_port ?? 22}
              onChange={(e) => setForm({ ...form, cli_port: Number(e.target.value) })} />
          </label>
          <label className="block">
            <span className="text-xs text-text-muted block mb-1">SNMP port / version</span>
            <div className="flex gap-1">
              <input type="number" className="input w-1/2 font-mono" value={form.snmp_port ?? 161}
                onChange={(e) => setForm({ ...form, snmp_port: Number(e.target.value) })} />
              <select className="input w-1/2" value={form.snmp_version ?? 'v2c'}
                onChange={(e) => setForm({ ...form, snmp_version: e.target.value })}>
                <option value="v1">v1</option>
                <option value="v2c">v2c</option>
                <option value="v3">v3</option>
              </select>
            </div>
          </label>
        </div>

        {/* Credentials section */}
        <div className="border-t border-border pt-3">
          <div className="text-xs font-semibold text-text-muted mb-2 flex items-center gap-1.5">
            <KeyRound size={11} />Credentials
            {isEdit && (
              <span className="text-text-faint font-normal">— laisse vide pour conserver les actuels</span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-text-muted block mb-1">CLI username</span>
              <input className="input w-full font-mono" value={form.cli_username ?? ''}
                onChange={(e) => setForm({ ...form, cli_username: e.target.value })}
                placeholder={isEdit && olt!.has_cli_credentials ? '••••••' : 'READONLY'}
                autoComplete="off" />
            </label>
            <label className="block">
              <span className="text-xs text-text-muted block mb-1">CLI password</span>
              <input type="password" className="input w-full font-mono" value={form.cli_password ?? ''}
                onChange={(e) => setForm({ ...form, cli_password: e.target.value })}
                placeholder={isEdit && olt!.has_cli_credentials ? '••••••' : '...'}
                autoComplete="new-password" />
            </label>
            <label className="block col-span-2">
              <span className="text-xs text-text-muted block mb-1">SNMP community {form.snmp_version === 'v2c' && '(v2c)'}</span>
              <input type="password" className="input w-full font-mono" value={form.snmp_community ?? ''}
                onChange={(e) => setForm({ ...form, snmp_community: e.target.value })}
                placeholder={isEdit && olt!.has_snmp_credentials ? '••••••' : 'public ou custom'}
                autoComplete="new-password" />
            </label>
          </div>
        </div>

        <label className="block">
          <span className="text-xs text-text-muted block mb-1">Notes</span>
          <textarea className="input w-full h-16 py-2" value={form.notes ?? ''}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Site critique, contact maint, etc." />
        </label>

        <div className="flex items-center justify-between pt-3 border-t border-border">
          {isEdit && (
            <button type="button"
              className="btn btn-sm text-danger border-danger/30 bg-danger/5 hover:bg-danger/15"
              disabled={destroy.isPending}
              onClick={() => { if (confirm(`Supprimer ${olt!.name} ?`)) destroy.mutate(); }}>
              <Trash2 size={10} />Supprimer
            </button>
          )}
          <div className="ml-auto flex gap-2">
            <button type="button" onClick={onClose} className="btn">Annuler</button>
            <button type="submit" disabled={upsert.isPending} className="btn btn-primary">
              {upsert.isPending ? '...' : (isEdit ? 'Enregistrer' : 'Créer')}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
